import React, { useEffect, useState } from "react";
import api, { API_BASE, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, Badge } from "@/components/Common";
import {
  FileText, FileSpreadsheet, FileType, Calendar as CalendarIcon, X,
} from "lucide-react";

export default function Reports() {
  const [reports, setReports] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: "", to: "" });

  useEffect(() => { api.get("/reports/list").then(({ data }) => setReports(data)); }, []);

  const download = async (key, fmt) => {
    const token = getToken();
    const params = new URLSearchParams();
    if (range.from) params.append("from_date", range.from);
    if (range.to) params.append("to_date", range.to);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const url = `${API_BASE}/reports/${key}.${fmt}${qs}`;
    const res = await fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    const suffix = range.from || range.to
      ? `_${range.from || "start"}_to_${range.to || "today"}`
      : "";
    a.download = `${key}${suffix}-${new Date().toISOString().slice(0, 10)}.${fmt}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const setPreset = (days) => {
    const to = today;
    const d = new Date(); d.setDate(d.getDate() - days);
    setRange({ from: d.toISOString().slice(0, 10), to });
  };
  const clearRange = () => setRange({ from: "", to: "" });
  const active = !!(range.from || range.to);

  return (
    <div>
      <PageHeader
        eyebrow="Insights"
        title="Reports & Analytics"
        subtitle="Generate operational, strategic and executive reports. Select a date range below — every export honours the same window."
      />

      {/* Date-range selector */}
      <div className="bg-white border border-gray-200 rounded-md p-4 mb-5"
        data-testid="reports-daterange">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1.5 text-emerald-900 font-display font-semibold text-sm">
            <CalendarIcon size={14} /> Report window
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">From</Label>
            <Input type="date" value={range.from} max={range.to || undefined}
              onChange={(e) => setRange({ ...range, from: e.target.value })}
              className="mt-1 h-9 w-40" data-testid="reports-from-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">To</Label>
            <Input type="date" value={range.to} min={range.from || undefined} max={today}
              onChange={(e) => setRange({ ...range, to: e.target.value })}
              className="mt-1 h-9 w-40" data-testid="reports-to-date" />
          </div>
          <div className="flex gap-1.5">
            {[
              { label: "Today",    days: 0 },
              { label: "Last 7d",  days: 6 },
              { label: "Last 30d", days: 29 },
              { label: "Last 90d", days: 89 },
              { label: "YTD",      days: null },
            ].map((p) => (
              <button key={p.label} type="button"
                onClick={() => {
                  if (p.days === null) {
                    const jan = new Date(new Date().getFullYear(), 0, 1);
                    setRange({ from: jan.toISOString().slice(0, 10), to: today });
                  } else setPreset(p.days);
                }}
                className="h-9 px-3 rounded-md text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 hover:bg-emerald-100 transition-colors"
                data-testid={`reports-preset-${p.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                {p.label}
              </button>
            ))}
          </div>
          {active && (
            <Button variant="ghost" size="sm" onClick={clearRange}
              className="text-red-700 hover:bg-red-50 h-9"
              data-testid="reports-clear-range">
              <X size={12} className="mr-1" /> Clear
            </Button>
          )}
          <div className="ml-auto">
            {active ? (
              <Badge tone="emerald">
                {range.from || "start"} → {range.to || today}
              </Badge>
            ) : (
              <Badge tone="slate">All time (default)</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
        {reports.map((r) => (
          <div key={r.key} className="bg-white border border-gray-200 rounded-md p-5 hover:shadow-sm transition-shadow">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-emerald-50 text-emerald-900 border border-emerald-200 flex items-center justify-center">
                <FileText size={18} />
              </div>
              <div className="flex-1">
                <div className="font-display font-semibold text-gray-900">{r.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">/{r.key}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <Button onClick={() => download(r.key, "csv")} variant="outline" size="sm"
                className="border-emerald-900 text-emerald-900 hover:bg-emerald-50"
                data-testid={`report-${r.key}-csv`}>
                <FileType size={12} className="mr-1" /> CSV
              </Button>
              <Button onClick={() => download(r.key, "xlsx")} variant="outline" size="sm"
                className="border-emerald-900 text-emerald-900 hover:bg-emerald-50"
                data-testid={`report-${r.key}-xlsx`}>
                <FileSpreadsheet size={12} className="mr-1" /> Excel
              </Button>
              <Button onClick={() => download(r.key, "pdf")} variant="outline" size="sm"
                className="border-emerald-900 text-emerald-900 hover:bg-emerald-50"
                data-testid={`report-${r.key}-pdf`}>
                <FileText size={12} className="mr-1" /> PDF
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
